import { AnimatePresence } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import Button from '@/components/Button'
import Loader from '@/components/Loader'
import CartButton from '@/features/Checkout/components/SummaryBar/CartButton'
import SummaryBar from '@/features/Checkout/components/SummaryBar/SummaryBar'
import useLayout from '@/layout/useLayout'
import View from '@/layout/View'
import type { ProductCategory } from '@/lib/api.types'
import { getCategoryId, useCategoriesQuery, useCategoryAvailabilityQuery, useTicketsQuery } from '@/lib/queries'

import useCheckout from '../Checkout/hooks/useCheckout'
import Categories from './components/Categories'
import type { CategoryUiData } from './components/Categories'
import ProductsList from './components/ProductsList'
import TicketsList from './components/TicketsList'
import { TicketsFooter, TicketsOuter, TicketsWrapper } from './Tickets.styles'

interface CategoryUiDataSortable extends CategoryUiData {
  sortOrder: number
}

const toCategoryUiData = (category: ProductCategory): CategoryUiDataSortable | null => {
  const categoryId = getCategoryId(category)
  if (categoryId === null) {
    return null
  }

  const typedCategory = category as {
    name?: unknown
    description?: unknown
    imageUrl?: unknown
    sortOrder?: unknown
  }

  return {
    id: categoryId,
    name: typeof typedCategory.name === 'string' ? typedCategory.name : '',
    description: typeof typedCategory.description === 'string' ? typedCategory.description : '',
    imageUrl: typeof typedCategory.imageUrl === 'string' ? typedCategory.imageUrl : undefined,
    sortOrder: Number(typedCategory.sortOrder) || 0
  }
}

const Tickets = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { data: categoriesData, isLoading: isCategoriesLoading } = useCategoriesQuery()
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | string | null>(null)
  const { cart, checkoutStartedAt, setCheckoutStartedAt } = useCheckout()
  const { accessibilityMode, appData } = useLayout()

  const categories = useMemo(
    () =>
      (categoriesData || [])
        .map(toCategoryUiData)
        .filter((category): category is CategoryUiDataSortable => Boolean(category))
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [categoriesData]
  )

  useEffect(() => {
    if (categories.length === 1) {
      setSelectedCategoryId(categories[0].id)
    }
  }, [categories])

  useEffect(() => {
    if (cart.length === 0 && checkoutStartedAt === null) {
      setCheckoutStartedAt(Date.now())
    }
  }, [cart.length, setCheckoutStartedAt, checkoutStartedAt])

  const resolvedSelectedCategoryId = selectedCategoryId ?? (categories.length === 1 ? categories[0].id : null)

  const activeCategory = useMemo(
    () => categories.find(category => String(category.id) === String(resolvedSelectedCategoryId)) || null,
    [categories, resolvedSelectedCategoryId]
  )
  const { data: ticketsData, isLoading: isTicketsLoading } = useTicketsQuery(resolvedSelectedCategoryId)
  useCategoryAvailabilityQuery(resolvedSelectedCategoryId)
  const products = ticketsData?.products || []
  const currency = appData?.currency
  const shouldShowCategories = resolvedSelectedCategoryId === null
  const shouldShowProductsList = resolvedSelectedCategoryId !== null && !isTicketsLoading && products.length > 1
  const shouldShowTicketsList = resolvedSelectedCategoryId !== null && !isTicketsLoading && products.length <= 1

  const handleBack = () => {
    if (categories.length > 1 && selectedCategoryId !== null) {
      setSelectedCategoryId(null)
      return
    }

    navigate('/')
  }

  if (isCategoriesLoading) {
    return <Loader cover />
  }

  return (
    <TicketsWrapper>
      <TicketsOuter>
        <AnimatePresence mode="wait">
          {shouldShowCategories && (
            <View key="categories">
              <Categories categories={categories} onSelect={categoryId => setSelectedCategoryId(categoryId)} />
            </View>
          )}

          {shouldShowProductsList && (
            <View key={`products-${String(resolvedSelectedCategoryId)}`}>
              <ProductsList
                categoryId={resolvedSelectedCategoryId}
                categoryName={activeCategory?.name || ''}
                coverImageUrl={activeCategory?.imageUrl}
                products={products}
                accessibilityMode={accessibilityMode}
                currency={currency}
                simpleHero={categories.length === 1}
              />
            </View>
          )}

          {shouldShowTicketsList && (
            <View key={`tickets-${String(resolvedSelectedCategoryId)}`}>
              <TicketsList
                categoryName={products[0]?.name || activeCategory?.name || ''}
                coverImageUrl={activeCategory?.imageUrl}
                products={products}
                accessibilityMode={accessibilityMode}
                currency={currency}
              />
            </View>
          )}
        </AnimatePresence>

        {resolvedSelectedCategoryId !== null && isTicketsLoading && <Loader cover />}
      </TicketsOuter>
      <TicketsFooter>
        <SummaryBar
          hideTotal
          actionBack={
            <Button variant="secondary" onClick={handleBack}>
              {t('cancel')}
            </Button>
          }
          actionSubmit={<CartButton onClick={() => navigate('/checkout')} disabled={cart.length === 0} />}
        />
      </TicketsFooter>
    </TicketsWrapper>
  )
}

export default Tickets
